import { Node } from 'reactflow';
import { workflowItems } from '../../pages/workflow';
import { WorkflowPatch, WorkflowSnapshot } from './types';

const CRITICAL_ACTIONS = new Set([
  'start', 'webhook',
  'whatsappListener', 'whatsappCloudTrigger', 'metaListenerTrigger',
  'shopifyTrigger', 'odooTrigger', 'easyOrdersTrigger', 'bostaTrigger', 'woocommerceTrigger',
  'sqlTrigger', 'googleDriveTrigger', 'googleCalendarTrigger', 'slackTrigger', 'teamsTrigger', 'sharepointTrigger', 'oneDriveTrigger',
  'typeformTrigger', 'googleFormTrigger', 'outlookTrigger', 'imapTrigger',
]);

export function enrichNodeFromCatalog(node: any): any {
  const action = workflowItems.find((item) => item.action === node?.data?.action);
  if (!action) return node;
  return {
    ...node,
    type: node.type || 'customNode',
    data: {
      ...node.data,
      executeRun: action.executeRun,
      background: action.background,
      icon: action.icon,
      postions: action.postions,
      ...(action.variablesRef ? { variablesRef: action.variablesRef } : {}),
    },
  };
}

export function findCriticalRemovals(nodes: Node[], patch: WorkflowPatch): string[] {
  const removeIds = new Set(
    patch.operations.filter((o) => o.op === 'remove_node').map((o: any) => o.id as string)
  );
  if (patch.operations.some((o) => o.op === 'replace_graph')) {
    const replace = patch.operations.find((o) => o.op === 'replace_graph') as any;
    const nextIds = new Set((replace.nodes || []).map((n: any) => String(n.id)));
    return nodes
      .filter((n) => CRITICAL_ACTIONS.has(n.data?.action) && !nextIds.has(n.id))
      .map((n) => n.id);
  }
  return nodes
    .filter((n) => removeIds.has(n.id) && CRITICAL_ACTIONS.has(n.data?.action))
    .map((n) => n.id);
}

export function validateAndBuildNextGraph(
  current: WorkflowSnapshot,
  patch: WorkflowPatch,
  options?: { allowCriticalDelete?: boolean }
): { ok: true; next: WorkflowSnapshot } | { ok: false; error: string } {
  try {
    const critical = findCriticalRemovals(current.nodes, patch);
    if (critical.length && !options?.allowCriticalDelete) {
      return {
        ok: false,
        error: `Critical trigger/start node(s) would be removed (${critical.join(', ')}). Extra confirmation required.`,
      };
    }

    // Preferred: atomic full graph from agent
    const replaceOp = patch.operations.find((o) => o.op === 'replace_graph') as any;
    if (replaceOp) {
      const nodes = (replaceOp.nodes || []).map(enrichNodeFromCatalog);
      const edges = (replaceOp.edges || []).map((e: any) => ({
        ...e,
        type: e.type || 'customEdge',
        animated: true,
      }));
      if (!Array.isArray(nodes) || !Array.isArray(edges)) {
        return { ok: false, error: 'Invalid replace_graph payload.' };
      }
      return { ok: true, next: { nodes, edges } };
    }

    let nodes = current.nodes.map((n) => ({ ...n, data: { ...n.data } }));
    let edges = [...current.edges];

    for (const op of patch.operations) {
      if (op.op === 'add_node') {
        const enriched = enrichNodeFromCatalog(op.node);
        if (!enriched?.id || !enriched?.data?.action) {
          return { ok: false, error: 'add_node missing id or action.' };
        }
        if (!workflowItems.find((i) => i.action === enriched.data.action)) {
          return { ok: false, error: `Unknown node action: ${enriched.data.action}` };
        }
        if (nodes.some((n) => n.id === enriched.id)) {
          return { ok: false, error: `Node id already exists: ${enriched.id}` };
        }
        nodes = [...nodes, enriched];
      } else if (op.op === 'update_node') {
        const idx = nodes.findIndex((n) => n.id === op.id);
        if (idx < 0) return { ok: false, error: `update_node target missing: ${op.id}` };
        const prev = nodes[idx];
        nodes[idx] = {
          ...prev,
          ...op.changes,
          data: {
            ...prev.data,
            ...(op.changes.data || {}),
            value: {
              ...prev.data?.value,
              ...(op.changes.data?.value || op.changes.value || {}),
            },
          },
          position: op.changes.position || prev.position,
        };
      } else if (op.op === 'remove_node') {
        if (!nodes.some((n) => n.id === op.id)) {
          return { ok: false, error: `remove_node target missing: ${op.id}` };
        }
        nodes = nodes.filter((n) => n.id !== op.id);
        edges = edges.filter((e) => e.source !== op.id && e.target !== op.id);
      } else if (op.op === 'add_edge') {
        const edge = { ...op.edge, type: op.edge.type || 'customEdge', animated: true };
        if (!edge.id || !edge.source || !edge.target) {
          return { ok: false, error: 'add_edge missing id/source/target.' };
        }
        if (!nodes.some((n) => n.id === edge.source) || !nodes.some((n) => n.id === edge.target)) {
          return { ok: false, error: 'add_edge references missing node.' };
        }
        edges = [...edges.filter((e) => e.id !== edge.id), edge];
      } else if (op.op === 'remove_edge') {
        edges = edges.filter((e) => e.id !== op.id);
      } else if (op.op === 'layout') {
        nodes = nodes.map((n) =>
          op.positions[n.id] ? { ...n, position: { ...op.positions[n.id] } } : n
        );
      }
    }

    return { ok: true, next: { nodes, edges } };
  } catch (err: any) {
    return { ok: false, error: err?.message || 'Failed to build next graph.' };
  }
}

export function summarizePatch(patch: WorkflowPatch) {
  const counts = {
    addedNodes: 0,
    updatedNodes: 0,
    removedNodes: 0,
    addedEdges: 0,
    removedEdges: 0,
    layout: false,
    replace: false,
  };
  for (const op of patch.operations) {
    if (op.op === 'add_node') counts.addedNodes += 1;
    if (op.op === 'update_node') counts.updatedNodes += 1;
    if (op.op === 'remove_node') counts.removedNodes += 1;
    if (op.op === 'add_edge') counts.addedEdges += 1;
    if (op.op === 'remove_edge') counts.removedEdges += 1;
    if (op.op === 'layout') counts.layout = true;
    if (op.op === 'replace_graph') {
      counts.replace = true;
      counts.addedNodes = (op as any).nodes?.length || 0;
      counts.addedEdges = (op as any).edges?.length || 0;
    }
  }
  return counts;
}

export function maskSecret(value?: string): string {
  if (!value) return '';
  if (value.length <= 8) return '••••••••';
  return `${value.slice(0, 4)}…${value.slice(-4)}`;
}
